"use client";
import { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Loader2, RefreshCcw, Undo2 } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/components/ui/use-toast";

const API_URL = import.meta.env.VITE_API_URL;

export default function ReturnsPage() {
    const { toast } = useToast();
    const [returns, setReturns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({});
    const [statusFilter, setStatusFilter] = useState("all");

    const fetchReturns = async () => {
        setLoading(true);
        try {
            const params = { page, limit: 20 };
            if (statusFilter !== "all") params.status = statusFilter;

            const res = await axios.get(`${API_URL}purchases/returns`, {
                params,
                withCredentials: true,
            });
            setReturns(res.data?.data || []);
            setPagination(res.data?.pagination || {});
        } catch (err) {
            console.error("Failed to fetch returns:", err);
            toast({ variant: "destructive", title: "Error", description: "Failed to fetch returns" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReturns();
    }, [page, statusFilter]);

    const statusColor = (status) => {
        switch (status) {
            case "completed": return "bg-green-100 text-green-800";
            case "approved": return "bg-blue-100 text-blue-800";
            case "pending": return "bg-yellow-100 text-yellow-800";
            case "rejected": return "bg-red-100 text-red-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    return (
        <div className="mx-auto p-4 space-y-6">
            <div className="flex flex-wrap justify-between items-center gap-4">
                <div className="flex items-center gap-4">
                    <h1 className="text-3xl font-bold">Purchase Returns</h1>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[130px]">
                            <SelectValue placeholder="All Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={fetchReturns} disabled={loading}>
                        <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                    <a href="/dashboard/purchases/returns-all/returns/add">
                        <Button>
                            <Undo2 className="h-4 w-4 mr-2" />
                            Create Return
                        </Button>
                    </a>
                </div>
            </div>

            <Card className="p-0">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Return #</TableHead>
                            <TableHead>Purchase Ref</TableHead>
                            <TableHead>Supplier</TableHead>
                            <TableHead>Items</TableHead>
                            <TableHead>Total</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Processed By</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} className="h-24 text-center">
                                    <span className="flex items-center justify-center gap-2">
                                        <Loader2 className="animate-spin" /> Loading returns...
                                    </span>
                                </TableCell>
                            </TableRow>
                        ) : returns.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                                    <div className="flex flex-col items-center gap-2">
                                        <Undo2 className="h-8 w-8 opacity-50" />
                                        No returns found.
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            returns.map((ret) => (
                                <TableRow
                                    key={ret.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() => window.location.href = `/dashboard/purchases/returns-all/returns/${ret.id}`}
                                >
                                    <TableCell>{format(new Date(ret.createdAt), "dd MMM yyyy")}</TableCell>
                                    <TableCell className="font-mono">{ret.return_number || '-'}</TableCell>
                                    <TableCell>{ret.Purchase?.invoice_number || '-'}</TableCell>
                                    <TableCell>{ret.Purchase?.Supplier?.name || 'N/A'}</TableCell>
                                    <TableCell>{ret.items?.length || 0} items</TableCell>
                                    <TableCell className="font-medium">
                                        GHS {parseFloat(ret.total || 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell>
                                        <Badge className={statusColor(ret.status)} variant="outline">
                                            {ret.status?.toUpperCase()}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>{ret.processedBy?.username || 'N/A'}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>

                {/* Pagination */}
                <div className="flex items-center justify-end space-x-2 py-4 px-4">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1 || loading}
                    >
                        Previous
                    </Button>
                    <div className="text-sm text-center min-w-[100px]">
                        Page {page} of {pagination?.totalPages || 1}
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => p + 1)}
                        disabled={page >= (pagination?.totalPages || 1) || loading}
                    >
                        Next
                    </Button>
                </div>
            </Card>
        </div>
    );
}
